import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  FiFile,
  FiPlus,
  FiSend,
  FiX,
  FiDownload,
  FiCheck,
  FiLayout,
  FiPaperclip,
  FiBold,
  FiItalic,
  FiUnderline,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiList,
  FiEye,
  FiEyeOff,
  FiLoader,
  FiSave,
  FiUpload,
} from "react-icons/fi";
import { MdFormatListNumbered, MdFormatColorText } from "react-icons/md";
import "./styles/QnrDesign.css";
import config from "../configs/config";
import { useToast } from "../contexts/ToastContext";

export default function QnrDesign() {
  const { toast } = useToast();
  const [prompt, setPrompt] = useState("");
  const [referenceFile, setReferenceFile] = useState(null);
  const [output, setOutput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [showFormatDropdown, setShowFormatDropdown] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [showUploadNotification, setShowUploadNotification] = useState(false);

  const [showSplitView, setShowSplitView] = useState(false);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [isLoadingDocx, setIsLoadingDocx] = useState(false);
  const [splitRatio, setSplitRatio] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [showFormattingMenu, setShowFormattingMenu] = useState(false);

  const [isExporting, setIsExporting] = useState(false);
  const [currentQuestionnaire, setCurrentQuestionnaire] = useState(null);


  // Sidebar states
  const [savedQuestionnaires, setSavedQuestionnaires] = useState([]);
  const [showNameInput, setShowNameInput] = useState(false);
  const [newQnrName, setNewQnrName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessingSidebar, setIsProcessingSidebar] = useState(false);

  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  const sidebarFileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const outputRef = useRef(null);
  const formattingMenuRef = useRef(null);

  useEffect(() => {
    console.log("Selected format changed:", selectedFormat);
  }, [selectedFormat]);

  useEffect(() => {
    if (outputRef.current && output && hasGenerated) {
      outputRef.current.innerHTML = output;
    }
  }, [output, hasGenerated]);

  useEffect(() => {
    function handleClickOutsideFormatting(event) {
      if (
        formattingMenuRef.current &&
        !formattingMenuRef.current.contains(event.target)
      ) {
        setShowFormattingMenu(false);
      }
    }

    if (showFormattingMenu) {
      document.addEventListener("mousedown", handleClickOutsideFormatting);
      return () =>
        document.removeEventListener("mousedown", handleClickOutsideFormatting);
    }
  }, [showFormattingMenu]);

  useEffect(() => {
    fetchSavedQuestionnaires();
  }, []); // Empty dependency array means it runs once on component mount

  // Add this function to fetch questionnaires from the database
  // 1. Fix fetchSavedQuestionnaires - Add better error handling and logging
  const fetchSavedQuestionnaires = async () => {
    try {
      setIsProcessingSidebar(true);

      console.log(
        "🔍 Fetching questionnaires from:",
        `${config.API_BASE_URL}/get-questionnaires`
      );

      const response = await fetch(
        `${config.API_BASE_URL}/get-questionnaires`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      console.log("📡 Response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Response error:", errorText);
        throw new Error("Failed to fetch questionnaires");
      }

      const data = await response.json();
      console.log("📦 Received data:", data);

      if (data.success && data.questionnaires) {
        // Transform the data to match your frontend structure
        const transformedQuestionnaires = data.questionnaires.map((qnr) => {
          // Parse sqgroup_json if it's a string
          let jsonData = qnr.sqgroup_json;
          if (typeof jsonData === "string") {
            try {
              jsonData = JSON.parse(jsonData);
            } catch (e) {
              console.error(`Failed to parse JSON for ${qnr.filename}:`, e);
              jsonData = [];
            }
          }

          return {
            id: qnr.id,
            name: qnr.filename.replace(/\.(docx|doc)$/i, ""), // Remove extension
            json: jsonData,
            totalQuestions: Array.isArray(jsonData) ? jsonData.length : 0,
            createdAt: qnr.created_at || new Date().toISOString(),
          };
        });

        console.log(
          "✅ Transformed questionnaires:",
          transformedQuestionnaires
        );
        setSavedQuestionnaires(transformedQuestionnaires);
        console.log(
          `✅ Loaded ${transformedQuestionnaires.length} questionnaires`
        );
      } else {
        console.warn("⚠️ No questionnaires in response or success=false");
        setSavedQuestionnaires([]);
      }
    } catch (error) {
      console.error("❌ Error fetching questionnaires:", error);
      // Show error but don't alert on mount
      setSavedQuestionnaires([]);
    } finally {
      setIsProcessingSidebar(false);
    }
  };

  // 2. Fix handleSaveQuestionnaire
  

  const convertJsonToHtml = (jsonData) => {
    let html = "";

    jsonData.forEach((question, index) => {
      // Question number and title
      html += `<h2 style="font-size: 16px;">${
        question.Question_number || `Q${index + 1}`
      }</h2>`;
      html += `<p style="font-size: 12px;"><strong>${question.Qtitle}</strong></p>`;

      // Instructions
      if (question.instructions && question.instructions.length > 0) {
        question.instructions.forEach((instruction) => {
          html += `<p style="font-size: 11px;">${instruction}</p>`;
        });
      }

      // Handle different question types
      if (
        question.type === "Numeric Grid" ||
        (question.rows &&
          question.rows.length > 0 &&
          question.columns &&
          question.columns.length > 0)
      ) {
        // Grid question with table
        html +=
          '<table style="border-collapse: collapse; width: 100%; margin: 2px 0; font-size: 10px; table-layout: fixed;">';

        // Table header
        html += "<thead><tr>";
        html +=
          '<th style="border: 1px solid #333; padding: 6px; background-color: #e0e0e0; font-size: 10px; word-wrap: break-word; white-space: normal; width: 20%;"></th>';
        question.columns.forEach((col) => {
          html += `<th style="border: 1px solid #333; padding: 6px; background-color: #e0e0e0; font-size: 10px; word-wrap: break-word; white-space: normal;">${col.text}</th>`;
        });
        html += "</tr></thead>";

        // Table body
        html += "<tbody>";
        question.rows.forEach((row) => {
          html += "<tr>";
          html += `<td style="border: 1px solid #333; padding: 6px; font-size: 10px; word-wrap: break-word; white-space: normal;"><strong>${row.text}</strong></td>`;
          question.columns.forEach(() => {
            html +=
              '<td style="border: 1px solid #333; padding: 6px; text-align: center; font-size: 10px; word-wrap: break-word; white-space: normal;"></td>';
          });
          html += "</tr>";
        });
        html += "</tbody></table>";
      } else if (question.rows && question.rows.length > 0) {
        // Single/Multiple Response with rows only
        html += '<ul style="font-size: 11px;">';
        question.rows.forEach((row) => {
          const symbol = question.type === "Single Response" ? "○" : "☐";
          html += `<li>${symbol} ${row.text}</li>`;
        });
        html += "</ul>";
      } else if (question.type === "Numeric") {
        // Numeric input
        html +=
          '<p style="margin: 10px 0; font-size: 11px;">__________ (Numeric response)</p>';
      }

      // Add spacing between questions
      html += "<br><br>";
    });

    return html;
  };

  const convertHtmlToJson = (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const questions = [];
    let currentQuestion = null;

    const allElements = Array.from(doc.body.children);

    allElements.forEach((element) => {
      const tagName = element.tagName.toLowerCase();
      const textContent = element.textContent.trim();

      // Detect question headers (h2, h3)
      if (tagName === "h2" || tagName === "h3") {
        if (currentQuestion) {
          questions.push(currentQuestion);
        }

        currentQuestion = {
          Question_number: textContent,
          Qtitle: "",
          instructions: [],
          type: "Single Response",
          rows: [],
          columns: [],
          statements: [],
          logics: "",
          _order: questions.length + 1,
        };
      }
      // Detect question title (bold text in paragraph)
      else if (
        currentQuestion &&
        tagName === "p" &&
        element.querySelector("strong")
      ) {
        const strongText = element.querySelector("strong").textContent;
        currentQuestion.Qtitle = strongText;
      }
      // Detect instructions
      else if (
        currentQuestion &&
        tagName === "p" &&
        !element.querySelector("strong")
      ) {
        if (textContent && !textContent.includes("__________")) {
          currentQuestion.instructions.push(textContent);
        }
      }
      // Detect tables (grids)
      else if (currentQuestion && tagName === "table") {
        const rows = Array.from(element.querySelectorAll("tbody tr"));
        const headers = Array.from(element.querySelectorAll("thead th"));

        const columns = headers.slice(1).map((th, idx) => ({
          index: `c${idx + 1}`,
          text: th.textContent.trim(),
        }));

        const rowData = rows.map((tr, idx) => {
          const cells = Array.from(tr.querySelectorAll("td"));
          return {
            index: `r${idx + 1}`,
            text: cells[0]?.textContent.trim() || "",
          };
        });

        currentQuestion.rows = rowData;
        currentQuestion.columns = columns;
        currentQuestion.type =
          columns.length > 1 ? "SingleGrid" : "Single Response";
      }
      // Detect lists (single/multiple choice)
      else if (currentQuestion && tagName === "ul") {
        const listItems = Array.from(element.querySelectorAll("li"));
        const rows = listItems.map((li, idx) => {
          let text = li.textContent.trim();
          // Remove radio/checkbox symbols
          text = text.replace(/^[○☐✓]\s*/, "");

          return {
            index: `r${idx + 1}`,
            text: text,
          };
        });

        currentQuestion.rows = rows;
        const hasCheckbox = listItems.some((li) =>
          li.textContent.includes("☐")
        );
        currentQuestion.type = hasCheckbox
          ? "Multiple Response"
          : "Single Response";
      }
      // Detect numeric input
      else if (currentQuestion && textContent.includes("__________")) {
        currentQuestion.type = "Numeric";
        currentQuestion.oeParameters = { min: "", max: "", isHidden: false };
      }
    });

    if (currentQuestion) {
      questions.push(currentQuestion);
    }

    return questions; // Return as array
  };

  const exportToSurveyBuilder = async () => {
    const htmlContent = outputRef.current?.innerHTML || output;

    if (!htmlContent.trim()) {
      toast.warning("No content to export");
      return;
    }

    setIsExporting(true);

    try {
      const jsonArray = convertHtmlToJson(htmlContent);

      if (jsonArray.length === 0) {
        throw new Error("No questions found in the document");
      }

      console.log("📤 Converted to JSON array:", jsonArray);

      // ✅ Use the current questionnaire's filename or create a temp one
      const filename = currentQuestionnaire?.name || "temp_unsaved_qnr";
      const questionnaireId = currentQuestionnaire?.id || null;

      // Send to backend for storage
      const response = await fetch(`${config.API_BASE_URL}/store-qnr-export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          filename: filename,
          questionnaireId: questionnaireId,
          data: jsonArray,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to store export data");
      }

      const result = await response.json();
      console.log("✅ Export stored:", result);

      // Store the export identifier for Survey Builder
      localStorage.setItem("qnr_export_filename", filename);
      localStorage.setItem("qnr_export_id", result.questionnaireId || filename);

      // Also try window object as fallback for small datasets
      if (jsonArray.length <= 10) {
        window.__QNR_EXPORT__ = jsonArray;
      }

      toast.success(`Successfully updated "${filename}"! ${jsonArray.length} questions prepared for Survey Builder.`);
    } catch (error) {
      toast.error(`Export failed: ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Sidebar functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Extract filename without extension
      const fileNameWithoutExt = file.name.replace(/\.(docx|doc)$/i, "");
      setSelectedFile(file);
      setNewQnrName(fileNameWithoutExt);
      setShowNameInput(true);
    }
  };

  const handleSaveQuestionnaire = async () => {
    if (!selectedFile) {
      toast.warning("Please select a DOCX file first");
      return;
    }

    if (!newQnrName.trim()) {
      toast.warning("Please enter a name for this questionnaire");
      return;
    }

    setIsProcessingSidebar(true);

    try {
      const formData = new FormData();
      formData.append("questionnaire", selectedFile);
      formData.append("model", "gemini");
      formData.append("questionnaire_name", newQnrName);

      const response = await fetch(
        `${config.API_BASE_URL}/process-questionnaire-to-json`,
        {
          method: "POST",
          body: formData,
          credentials: "include",
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to process questionnaire");
      }

      const data = await response.json();
      await fetchSavedQuestionnaires();

      toast.success(`Successfully saved! ${data.total_questions || data.sqgroup?.length || 0} questions extracted.`);

      setSelectedFile(null);
      setNewQnrName("");
      setShowNameInput(false);
      if (sidebarFileInputRef.current) {
        sidebarFileInputRef.current.value = "";
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsProcessingSidebar(false);
    }
  };

  const cancelNameInput = () => {
    setShowNameInput(false);
    setNewQnrName("");
    setSelectedFile(null);
    if (sidebarFileInputRef.current) {
      sidebarFileInputRef.current.value = "";
    }
  };

  const loadQuestionnaire = (qnr) => {
    const htmlContent = convertJsonToHtml(qnr.json);
    setOutput(htmlContent);
    setHasGenerated(true);
    setCurrentQuestionnaire(qnr);
  };


  const questionnaireFormats = {
    format1: {
      name: "Standard Market Research",
      description:
        "Comprehensive format with demographics, awareness, usage, and rating questions",
    },
    format2: {
      name: "Customer Satisfaction",
      description:
        "Focused on customer experience, satisfaction metrics, and NPS",
    },
  };

  const readWordFile = async (file) => {
    try {
      if (file.name.endsWith(".txt")) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const text = e.target.result;
            resolve({ text, html: text.replace(/\n/g, "<br>") });
          };
          reader.onerror = reject;
          reader.readAsText(file);
        });
      }

      if (file.name.endsWith(".docx") || file.name.endsWith(".doc")) {
        console.log("📤 Sending file to backend for extraction...");
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(`${config.API_BASE_URL}/extract-docx`, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || "Failed to extract document");
        }

        const data = await response.json();
        console.log("✅ Extracted from backend");
        return { text: data.content, html: data.content };
      }

      throw new Error(
        "Unsupported file format. Please use .txt, .doc, or .docx files."
      );
    } catch (error) {
      console.error("❌ Error reading file:", error);
      throw error;
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const content = await readWordFile(file);
        console.log("File uploaded successfully:", file.name);

        if (file.name.endsWith(".docx") || file.name.endsWith(".doc")) {
          const formData = new FormData();
          formData.append("file", file);

          const uploadResponse = await fetch(
            `${config.API_BASE_URL}/upload-reference-file`,
            {
              method: "POST",
              body: formData,
              credentials: "include",
            }
          );

          if (uploadResponse.ok) {
            const data = await uploadResponse.json();
            setUploadedFileId(data.file_id);
          }
        }

        setReferenceFile({ name: file.name, ...content });
        setShowUploadNotification(true);
        setTimeout(() => setShowUploadNotification(false), 3000);
      } catch (error) {
        toast.error("Error reading file: " + error.message);
      }
    }
  };

  const loadDocxPreview = async () => {
    if (!uploadedFileId) {
      toast.warning("No document available for preview");
      return;
    }

    try {
      setIsLoadingDocx(true);

      const response = await fetch(
        `${config.API_BASE_URL}/get_embed_url/${uploadedFileId}`,
        { credentials: "include" }
      );

      if (!response.ok) {
        throw new Error("Failed to get embed URL");
      }

      const data = await response.json();
      if (data.success) {
        setEmbedUrl(data.embed_url);
        setShowSplitView(true);
      } else {
        toast.error("Failed to load document preview");
      }
    } catch (error) {
      toast.error("Error loading document preview");
    } finally {
      setIsLoadingDocx(false);
    }
  };

  const toggleSplitView = () => {
    if (!uploadedFileId) {
      toast.warning("Please upload a DOCX file first");
      return;
    }

    if (!showSplitView && !embedUrl) {
      loadDocxPreview();
    } else {
      setShowSplitView(!showSplitView);
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newRatio =
        ((e.clientX - containerRect.left) / containerRect.width) * 100;
      setSplitRatio(Math.max(20, Math.min(80, newRatio)));
    },
    [isDragging]
  );

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  const selectFormat = (formatKey) => {
    console.log("Selecting format:", formatKey);
    setSelectedFormat(formatKey);
    setShowFormatDropdown(false);
  };

  const clearFormat = () => {
    setSelectedFormat(null);
  };

  const generateQuestionnaire = async () => {
    if (!prompt.trim()) {
      toast.warning("Please enter a prompt");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `${config.API_BASE_URL}/api/generate-questionnaire`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            prompt: prompt,
            referenceContent: referenceFile?.html,
            selectedFormat: selectedFormat,
            hasGenerated: hasGenerated,
            existingOutput: output,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to generate questionnaire");
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Failed to generate questionnaire");
      }

      setOutput(data.content);
      setHasGenerated(true);
      setPrompt("");
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const exportToWord = () => {
    const contentToExport = outputRef.current
      ? outputRef.current.innerHTML
      : output;
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Questionnaire</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; padding: 20px; }
          table { border-collapse: collapse; width: 100%; margin: 10px 0; }
          th, td { border: 1px solid #000; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; }
        </style>
      </head>
      <body>${contentToExport}</body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "questionnaire.doc";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      generateQuestionnaire();
    }
  };

  const removeReference = () => {
    setReferenceFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const startNewQuestionnaire = () => {
    setOutput("");
    setHasGenerated(false);
    setPrompt("");
    setReferenceFile(null);
    setSelectedFormat(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowFormatDropdown(false);
      }
    }

    if (showFormatDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showFormatDropdown]);

  const applyFormat = (command, value = null) => {
    if (outputRef.current) {
      outputRef.current.focus();
      document.execCommand(command, false, value);
    }
  };

  return (
    <div className={`qnr-main-layout ${hasGenerated ? "has-generated" : ""}`}>
      {/* Permanent Sidebar */}
      <div className="sidebar-permanent">
        <div className="sidebar-header">
          <h2>Questionnaires</h2>
        </div>

        <div className="sidebar-content">
          {/* New Questionnaire Button */}
          {!showNameInput && (
            <>
              <input
                ref={sidebarFileInputRef}
                type="file"
                accept=".docx,.doc"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
              <button
                onClick={() => sidebarFileInputRef.current?.click()}
                className="new-qnr-btn"
                disabled={isProcessingSidebar}
              >
                <FiPlus size={20} />
              </button>
            </>
          )}

          {/* Name Input Form */}
          {showNameInput && (
            <div className="name-input-section">
              <label className="sidebar-label">Questionnaire Name</label>
              <input
                type="text"
                value={newQnrName}
                onChange={(e) => setNewQnrName(e.target.value)}
                placeholder="Enter name..."
                className="sidebar-input"
                disabled={isProcessingSidebar}
                autoFocus
              />
              <div className="name-input-actions">
                <button
                  onClick={handleSaveQuestionnaire}
                  disabled={isProcessingSidebar || !newQnrName.trim()}
                  className="save-btn"
                >
                  {isProcessingSidebar ? (
                    <>
                      <FiLoader className="spinner" size={14} />
                    </>
                  ) : (
                    <>
                      <FiSave size={14} />
                      Save
                    </>
                  )}
                </button>
                <button
                  onClick={cancelNameInput}
                  disabled={isProcessingSidebar}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Saved Questionnaires List */}
          {savedQuestionnaires.length > 0 && (
            <div className="qnr-list">
              <div className="qnr-list-header">Saved Questionnaires</div>
              {savedQuestionnaires.map((qnr) => (
                <div
                  key={qnr.id}
                  className="qnr-item"
                  onClick={() => loadQuestionnaire(qnr)}
                >
                  <div className="qnr-item-icon">
                    <FiFile size={16} />
                  </div>
                  <div className="qnr-item-content">
                    <div className="qnr-item-name">{qnr.name}</div>
                    <div className="qnr-item-meta">
                      {qnr.totalQuestions} questions
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {savedQuestionnaires.length === 0 && !showNameInput && (
            <div className="empty-state">
              <FiFile size={48} color="#d1d5db" />
              <p>No questionnaires yet</p>
              <p className="empty-state-hint">
                Click the + button to create one
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="qnr-main-content">
        <div className={`qnr-container ${hasGenerated ? "has-generated" : ""}`}>
          {!hasGenerated && <h1 className="qnr-title">Generate a QnR</h1>}

          {hasGenerated && (
            <div
              ref={containerRef}
              className={`split-container ${
                showSplitView ? "full-height" : "centered-view"
              }`}
              style={{
                display: showSplitView ? "flex" : "block",
                height: showSplitView ? "100vh" : "auto",
              }}
            >
              {/* Left side - output section */}
              <div
                className={`output-section ${!showSplitView ? "centered" : ""}`}
                style={{
                  width: showSplitView ? `${splitRatio}%` : "100%",
                  transition: isDragging ? "none" : "width 0.3s ease",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  height: showSplitView ? "100vh" : "auto",
                }}
              >
                {/* Formatting Toolbar */}
                <div className="formatting-toolbar">
                  <div className="toolbar-left">
                    {showSplitView && (
                      <>
                        <div
                          className="dropdown-wrapper"
                          style={{ position: "relative" }}
                          ref={formattingMenuRef}
                        >
                          <button
                            onClick={() =>
                              setShowFormattingMenu(!showFormattingMenu)
                            }
                            className="format-btn"
                            title="Formatting Tools"
                          >
                            <span style={{ fontSize: "12px" }}>🎨</span>
                          </button>

                          {showFormattingMenu && (
                            <div className="formatting-menu-dropdown">
                              <div className="formatting-menu-section">
                                <div className="formatting-menu-label">
                                  Text Style
                                </div>
                                <div className="formatting-menu-grid">
                                  <button
                                    onClick={() => {
                                      applyFormat("bold");
                                      setShowFormattingMenu(false);
                                    }}
                                    className="format-btn"
                                    title="Bold"
                                  >
                                    <FiBold size={12} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      applyFormat("italic");
                                      setShowFormattingMenu(false);
                                    }}
                                    className="format-btn"
                                    title="Italic"
                                  >
                                    <FiItalic size={12} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      applyFormat("underline");
                                      setShowFormattingMenu(false);
                                    }}
                                    className="format-btn"
                                    title="Underline"
                                  >
                                    <FiUnderline size={12} />
                                  </button>
                                  <div
                                    className="color-picker-wrapper"
                                    style={{ padding: "8px" }}
                                  >
                                    <MdFormatColorText
                                      size={16}
                                      className="color-icon"
                                    />
                                    <input
                                      type="color"
                                      onChange={(e) => {
                                        applyFormat(
                                          "foreColor",
                                          e.target.value
                                        );
                                        setShowFormattingMenu(false);
                                      }}
                                      className="color-picker"
                                      title="Text Color"
                                      style={{ width: "24px", height: "20px" }}
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="formatting-menu-section">
                                <div className="formatting-menu-label">
                                  Alignment
                                </div>
                                <div className="formatting-menu-grid">
                                  <button
                                    onClick={() => {
                                      applyFormat("justifyLeft");
                                      setShowFormattingMenu(false);
                                    }}
                                    className="format-btn"
                                    title="Align Left"
                                  >
                                    <FiAlignLeft size={16} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      applyFormat("justifyCenter");
                                      setShowFormattingMenu(false);
                                    }}
                                    className="format-btn"
                                    title="Align Center"
                                  >
                                    <FiAlignCenter size={16} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      applyFormat("justifyRight");
                                      setShowFormattingMenu(false);
                                    }}
                                    className="format-btn"
                                    title="Align Right"
                                  >
                                    <FiAlignRight size={16} />
                                  </button>
                                </div>
                              </div>

                              <div className="formatting-menu-section">
                                <div className="formatting-menu-label">
                                  Lists
                                </div>
                                <div className="formatting-menu-grid">
                                  <button
                                    onClick={() => {
                                      applyFormat("insertUnorderedList");
                                      setShowFormattingMenu(false);
                                    }}
                                    className="format-btn"
                                    title="Bullet List"
                                  >
                                    <FiList size={16} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      applyFormat("insertOrderedList");
                                      setShowFormattingMenu(false);
                                    }}
                                    className="format-btn"
                                    title="Numbered List"
                                  >
                                    <MdFormatListNumbered size={16} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="toolbar-divider"></div>
                      </>
                    )}

                    {!showSplitView && (
                      <>
                        <button
                          onClick={() => applyFormat("bold")}
                          className="format-btn"
                          title="Bold"
                        >
                          <FiBold size={12} />
                        </button>
                        <button
                          onClick={() => applyFormat("italic")}
                          className="format-btn"
                          title="Italic"
                        >
                          <FiItalic size={12} />
                        </button>
                        <button
                          onClick={() => applyFormat("underline")}
                          className="format-btn"
                          title="Underline"
                        >
                          <FiUnderline size={12} />
                        </button>

                        <div className="toolbar-divider"></div>

                        <button
                          onClick={() => applyFormat("justifyLeft")}
                          className="format-btn"
                          title="Align Left"
                        >
                          <FiAlignLeft size={12} />
                        </button>
                        <button
                          onClick={() => applyFormat("justifyCenter")}
                          className="format-btn"
                          title="Align Center"
                        >
                          <FiAlignCenter size={12} />
                        </button>
                        <button
                          onClick={() => applyFormat("justifyRight")}
                          className="format-btn"
                          title="Align Right"
                        >
                          <FiAlignRight size={12} />
                        </button>

                        <div className="toolbar-divider"></div>

                        <button
                          onClick={() => applyFormat("insertUnorderedList")}
                          className="format-btn"
                          title="Bullet List"
                        >
                          <FiList size={12} />
                        </button>
                        <button
                          onClick={() => applyFormat("insertOrderedList")}
                          className="format-btn"
                          title="Numbered List"
                        >
                          <MdFormatListNumbered size={12} />
                        </button>

                        <div className="toolbar-divider"></div>

                        <div className="color-picker-wrapper">
                          <MdFormatColorText size={12} className="color-icon" />
                          <input
                            type="color"
                            onChange={(e) =>
                              applyFormat("foreColor", e.target.value)
                            }
                            className="color-picker"
                            title="Text Color"
                          />
                        </div>

                        <div className="toolbar-divider"></div>
                      </>
                    )}
                  </div>

                  <div className="toolbar-right-vertical">
                    <button
                      onClick={exportToSurveyBuilder}
                      className="btn-success stacked-btn"
                      disabled={isExporting || !output.trim()}
                      style={{
                        backgroundColor: "#10b981",
                        opacity: isExporting || !output.trim() ? 0.5 : 1,
                      }}
                    >
                      {isExporting ? (
                        <>
                          <FiLoader size={12} className="spinner" />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <FiUpload size={12} />
                          To Survey
                        </>
                      )}
                    </button>

                    <button
                      onClick={startNewQuestionnaire}
                      className="btn-secondary stacked-btn"
                    >
                      <FiPlus size={12} />
                      New Qnr
                    </button>

                    <button
                      onClick={exportToWord}
                      className="btn-primary stacked-btn"
                    >
                      <FiDownload size={12} />
                      Export
                    </button>
                  </div>
                </div>

                {/* Editable Content Area */}
                <div
                  ref={outputRef}
                  contentEditable
                  suppressContentEditableWarning={true}
                  className="content-area"
                />
              </div>

              {/* Divider */}
              {showSplitView && (
                <div
                  onMouseDown={handleMouseDown}
                  style={{
                    width: "5px",
                    cursor: "col-resize",
                    backgroundColor: "#e5e7eb",
                    position: "relative",
                    flexShrink: 0,
                    transition: isDragging ? "none" : "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#3b82f6";
                  }}
                  onMouseLeave={(e) => {
                    if (!isDragging) {
                      e.currentTarget.style.backgroundColor = "#e5e7eb";
                    }
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "20px",
                      height: "40px",
                      backgroundColor: isDragging ? "#3b82f6" : "#9ca3af",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "10px",
                    }}
                  >
                    ⋮
                  </div>
                </div>
              )}

              {/* Right side - Document preview */}
              {showSplitView && (
                <div
                  style={{
                    width: `${100 - splitRatio}%`,
                    backgroundColor: "#f9fafb",
                    display: "flex",
                    flexDirection: "column",
                    transition: isDragging ? "none" : "width 0.3s ease",
                    position: "relative",
                  }}
                >
                  {isDragging && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 9999,
                        cursor: "col-resize",
                      }}
                    />
                  )}

                  <div style={{ flex: 1, overflow: "hidden" }}>
                    {embedUrl ? (
                      <iframe
                        src={embedUrl}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        title="Document Preview"
                        style={{ border: "none" }}
                      />
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          color: "#6b7280",
                        }}
                      >
                        {isLoadingDocx
                          ? "Loading document..."
                          : "No preview available"}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Prompt Section */}
          <div
            className={`prompt-section ${hasGenerated ? "fixed" : "relative"}`}
          >
            {referenceFile && (!hasGenerated || showUploadNotification) && (
              <div className="indicator-card file-indicator">
                <div className="indicator-content">
                  <FiFile size={18} />
                  <span>
                    {referenceFile.name} ({referenceFile.text.length} chars)
                  </span>
                </div>
                <button onClick={removeReference} className="indicator-close">
                  <FiX size={20} />
                </button>
              </div>
            )}

            {selectedFormat && !hasGenerated && (
              <div className="indicator-card format-indicator">
                <div className="indicator-content">
                  <FiLayout size={12} />
                  <div>
                    <span className="format-name">
                      {questionnaireFormats[selectedFormat].name}
                    </span>
                    <div className="format-description">
                      {questionnaireFormats[selectedFormat].description}
                    </div>
                  </div>
                </div>
                <button onClick={clearFormat} className="indicator-close">
                  <FiX size={20} />
                </button>
              </div>
            )}

            {/* Search Bar */}
            <div className="search-bar">
              <div className="dropdown-wrapper" ref={dropdownRef}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFormatDropdown(!showFormatDropdown);
                  }}
                  className={`icon-btn ${selectedFormat ? "active" : ""}`}
                  title={
                    selectedFormat
                      ? `Selected: ${questionnaireFormats[selectedFormat]?.name}`
                      : "Select questionnaire format"
                  }
                >
                  <FiLayout size={22} />
                </button>

                {showFormatDropdown && (
                  <div
                    className="format-dropdown"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="dropdown-header">
                      Select Questionnaire Format
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        selectFormat("format1");
                      }}
                      className={`format-option ${
                        selectedFormat === "format1" ? "selected" : ""
                      }`}
                    >
                      <div className="format-option-header">
                        <FiLayout size={16} />
                        {questionnaireFormats.format1.name}
                        {selectedFormat === "format1" && <FiCheck size={14} />}
                      </div>
                      <div className="format-option-description">
                        {questionnaireFormats.format1.description}
                      </div>
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        selectFormat("format2");
                      }}
                      className={`format-option ${
                        selectedFormat === "format2" ? "selected" : ""
                      }`}
                    >
                      <div className="format-option-header">
                        <FiLayout size={16} />
                        {questionnaireFormats.format2.name}
                        {selectedFormat === "format2" && <FiCheck size={14} />}
                      </div>
                      <div className="format-option-description">
                        {questionnaireFormats.format2.description}
                      </div>
                    </button>
                  </div>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".doc,.docx,.txt"
                onChange={handleFileUpload}
                style={{ display: "none" }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="icon-btn"
                title="Add reference document"
              >
                <FiPaperclip size={22} />
              </button>

              <input
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={
                  hasGenerated ? "Modify questionnaire..." : "Ask anything"
                }
                disabled={isLoading}
                className="prompt-input"
              />

              <button
                onClick={generateQuestionnaire}
                disabled={isLoading || !prompt.trim()}
                className={`send-btn ${
                  isLoading || !prompt.trim() ? "disabled" : ""
                }`}
              >
                {isLoading ? <div className="spinner" /> : <FiSend size={20} />}
              </button>
              <button
                onClick={toggleSplitView}
                disabled={isLoadingDocx || !uploadedFileId}
                className="format-btn"
                title={showSplitView ? "Hide Document" : "Show Document"}
              >
                {isLoadingDocx ? (
                  <div className="spinner" />
                ) : showSplitView ? (
                  <FiEyeOff size={16} />
                ) : (
                  <FiEye size={16} />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
